'use client';

import { useEffect, useRef } from 'react';
import { useAppStore } from '@/features/shared/app-store';
import { fetchCart } from '@/features/cart/internal/helpers/fetch-cart';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { setBestGuessAddress } from '@/features/cart/internal/helpers/set-best-guess-address';
import { CartData } from '@/features/cart/shared/types/cart.types';
import { useCurrency } from '@/features/shared/hooks/use-currency';

export default function CartStoreHydrator() {
  const setCart = useAppStore((s) => s.setCart);
  const currencyCode = useCurrency();

  const previousWildMemberStatus = useRef<boolean | null>(null);
  const previousLoggedInStatus = useRef<boolean | null>(null);
  const { loading, authenticatedUser } = useUser();

  useEffect(() => {
    if (loading || !currencyCode) return;
    const currentWildMember = !!authenticatedUser?.isWildMember;
    const currentLoginStatus = !!authenticatedUser;
    const needsFirstFetch = previousLoggedInStatus.current === null;

    const membershipChanged =
      previousWildMemberStatus.current !== null &&
      previousWildMemberStatus.current !== currentWildMember;

    const loginStatusChanged =
      previousLoggedInStatus.current !== null &&
      previousLoggedInStatus.current !== currentLoginStatus;

    if (!needsFirstFetch && !membershipChanged && !loginStatusChanged) return;

    previousWildMemberStatus.current = authenticatedUser?.isWildMember || false;
    previousLoggedInStatus.current = !!authenticatedUser;

    (async () => {
      const emptyCart: CartData = {
        items: [],
        totalPrice: {
          type: 'total',
          value: 0,
          currencyCode,
        },
        cartId: null,
        currencyCode,
      };
      try {
        if (loginStatusChanged) {
          await setBestGuessAddress();
        }
        const cart = await fetchCart();
        setCart(cart ?? emptyCart);
      } catch (_) {
        console.debug('no stored cart', _);
        setCart(emptyCart);
      }
    })();
  }, [currencyCode, loading, authenticatedUser, setCart]);

  return null;
}
